import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  DataTable,
  FilterBar,
  Kpi,
  Pager,
  Select,
  StateBlock,
  type Column,
  type DateRangePresetId,
} from '@w7/ui'
import { useSubGames } from '@/api/hooks/reports/useSubGames'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'
import { RangePicker, presetOf } from '@/features/reports/RangePicker'
import { isoDay, money } from '@/lib/format'
import './vendor-dashboard.css'

/** Betfair vendor dashboard page size. */
const PAGE_SIZE = 20
const ALL = 'all'

type Row = {
  createdAt: string
  subgame: string
  event: string
  market: string
  marketId: string
  beforeCommission: number
  commission: number
  afterCommission: number
}

type Applied = {
  from: string
  to: string
  subgame: string
  event: string
  market: string
  run: number
}

function moneyCell(n: number) {
  const cls = n > 0 ? 'cm-money-pos' : n < 0 ? 'cm-money-neg' : undefined
  return <span className={cls}>{money(n, { symbol: false })}</span>
}

/**
 * Vendor Dashboard → Betfair from Strix BQ orders + settlement
 * (CM-API `POST /v1/vendor/betfair/bq-detail`).
 * Subgame: existing Account/getSubGames. Event/Market: betfair/bq-filter-options.
 * Chrome: FilterBar desk (Bet Ticker) + PL Statement DateRangePicker; KPIs below.
 */
export function BetfairPage() {
  const today = isoDay(new Date())
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [rangePreset, setRangePreset] = useState<DateRangePresetId>(() =>
    presetOf({ from: today, to: today }),
  )
  const [subgame, setSubgame] = useState(ALL)
  const [event, setEvent] = useState(ALL)
  const [market, setMarket] = useState(ALL)
  const [applied, setApplied] = useState<Applied>({
    from: today,
    to: today,
    subgame: '',
    event: '',
    market: '',
    run: 0,
  })
  const [page, setPage] = useState(1)

  // Same Sub Game catalog as PL Statement (SportsBook default via getSubGames).
  const subGamesQ = useSubGames('all')
  const subgameOptions = useMemo(
    () => [
      { value: ALL, label: 'All Subgame' },
      ...(subGamesQ.data ?? []).map((name) => ({ value: name, label: name })),
    ],
    [subGamesQ.data],
  )

  const filterSubgame = subgame !== ALL ? subgame : undefined
  const filtersQ = useQuery({
    queryKey: [
      'cm',
      'vendor',
      'betfair',
      'bq-filter-options',
      fromDate,
      toDate,
      filterSubgame ?? '',
    ],
    queryFn: () =>
      cmApi.betfairBqFilterOptions(authHeaders(), {
        fromDate,
        toDate,
        ...(filterSubgame ? { subgame: filterSubgame } : {}),
      }),
    enabled: Boolean(fromDate && toDate),
    staleTime: 60_000,
  })

  const eventOptions = useMemo(
    () => [
      { value: ALL, label: 'All Event' },
      ...(filtersQ.data?.events ?? []).map((name) => ({
        value: name,
        label: name,
      })),
    ],
    [filtersQ.data?.events],
  )
  const marketOptions = useMemo(
    () => [
      { value: ALL, label: 'All Market' },
      ...(filtersQ.data?.markets ?? []).map((name) => ({
        value: name,
        label: name,
      })),
    ],
    [filtersQ.data?.markets],
  )

  function applyDetails(next?: { from: string; to: string }) {
    const from = next?.from ?? fromDate
    const to = next?.to ?? toDate
    setPage(1)
    setApplied((prev) => ({
      from,
      to,
      subgame: subgame === ALL ? '' : subgame,
      event: event === ALL ? '' : event,
      market: market === ALL ? '' : market,
      run: prev.run + 1,
    }))
  }

  // Drop Event/Market picks that are no longer in the options list.
  useEffect(() => {
    if (event !== ALL && !(filtersQ.data?.events ?? []).includes(event)) {
      setEvent(ALL)
    }
    if (market !== ALL && !(filtersQ.data?.markets ?? []).includes(market)) {
      setMarket(ALL)
    }
  }, [filtersQ.data, event, market])

  const q = useQuery({
    queryKey: [
      'cm',
      'vendor',
      'betfair',
      'bq-detail',
      applied.from,
      applied.to,
      applied.subgame,
      applied.event,
      applied.market,
      applied.run,
      page,
      PAGE_SIZE,
    ],
    queryFn: () =>
      cmApi.betfairBqDetail(authHeaders(), {
        fromDate: applied.from,
        toDate: applied.to,
        ...(applied.subgame ? { subgame: applied.subgame } : {}),
        ...(applied.event ? { event: applied.event } : {}),
        ...(applied.market ? { market: applied.market } : {}),
        page,
        pageSize: PAGE_SIZE,
      }),
  })

  const rows = q.data?.rows ?? []
  const total = q.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const beforeTotal = q.data?.beforeCommissionTotal ?? 0
  const afterTotal = q.data?.afterCommissionTotal ?? 0

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        id: 'n',
        header: 'No.',
        width: 56,
        numeric: true,
        cell: (_r, i) => String((page - 1) * PAGE_SIZE + i + 1),
      },
      {
        id: 'createdAt',
        header: 'Created At',
        cell: (r) =>
          r.createdAt ? new Date(r.createdAt).toLocaleString() : '—',
        sortValue: (r) => r.createdAt ?? '',
      },
      {
        id: 'subgame',
        header: 'Subgame',
        cell: (r) => r.subgame || '—',
        sortValue: (r) => r.subgame || '',
      },
      {
        id: 'event',
        header: 'Event',
        cell: (r) => r.event || '—',
        sortValue: (r) => r.event || '',
      },
      {
        id: 'market',
        header: 'Market',
        cell: (r) => r.market || '—',
        sortValue: (r) => r.market || '',
      },
      {
        id: 'before',
        header: 'Before Commission',
        numeric: true,
        cell: (r) => moneyCell(r.beforeCommission),
        sortValue: (r) => r.beforeCommission,
      },
      {
        id: 'commission',
        header: 'Commission',
        numeric: true,
        cell: (r) => moneyCell(r.commission),
        sortValue: (r) => r.commission,
      },
      {
        id: 'after',
        header: 'After Commission',
        numeric: true,
        cell: (r) => moneyCell(r.afterCommission),
        sortValue: (r) => r.afterCommission,
      },
    ],
    [page],
  )

  return (
    <CmPanel
      title="Betfair"
      subtitle="Vendor Dashboard — Betfair from BigQuery"
      wide
    >
      <div className="cm-vendor-betfair">
        <div className="cm-vendor-betfair-toolbar">
          <FilterBar label="Betfair filters">
            <div className="cm-vendor-betfair-toolbar-filters">
              <RangePicker
                preset={rangePreset}
                value={{ from: fromDate, to: toDate }}
                onChange={(next) => {
                  setRangePreset(next.preset)
                  setFromDate(next.from)
                  setToDate(next.to)
                  applyDetails({ from: next.from, to: next.to })
                }}
              />
              <Select
                aria-label="Subgame"
                value={subgame}
                onChange={(e) => {
                  setSubgame(e.target.value)
                  setEvent(ALL)
                  setMarket(ALL)
                }}
                options={subgameOptions}
              />
              <Select
                aria-label="Event"
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                options={eventOptions}
              />
              <Select
                aria-label="Market"
                value={market}
                onChange={(e) => setMarket(e.target.value)}
                options={marketOptions}
              />
              <div className="cm-vendor-betfair-toolbar-actions">
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => applyDetails()}
                >
                  Get Details
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  icon="refresh"
                  busy={q.isFetching}
                  aria-label="Refresh"
                  onClick={() => void q.refetch()}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </FilterBar>
        </div>

        <div className="cm-vendor-filters-kpi cm-vendor-filters-kpi-pair cm-vendor-betfair-kpi">
          <Kpi
            label="After Commission:"
            value={q.isLoading && !q.data ? '—' : moneyCell(afterTotal)}
          />
          <Kpi
            label="Before Commission:"
            value={q.isLoading && !q.data ? '—' : moneyCell(beforeTotal)}
          />
        </div>
      </div>

      {q.isLoading && !rows.length ? (
        <StateBlock icon="sport" title="Loading Betfair…">
          Reading Betfair BQ settlements
        </StateBlock>
      ) : null}

      {q.isError ? (
        <StateBlock icon="warning" title="Could not load Betfair">
          {q.error instanceof Error
            ? q.error.message
            : 'Betfair detail request failed'}
        </StateBlock>
      ) : null}

      {!q.isLoading && !q.isError && rows.length === 0 ? (
        <StateBlock icon="search" title="No data found">
          No Betfair settlements for these filters (scoped to your CM
          company_master / agentId).
        </StateBlock>
      ) : null}

      {rows.length > 0 ? (
        <div className="cm-vendor-betfair-results">
          <div className="cm-vendor-betfair-table-wrap">
            <DataTable
              label="Betfair settlements"
              columns={columns}
              rows={rows}
              rowKey={(r, i) => `${r.marketId}-${i}`}
              density="compact"
              stickyOffset={0}
            />
          </div>
          <div className="cm-vendor-betfair-pager">
            <Pager
              page={page}
              pageCount={pageCount}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </div>
        </div>
      ) : null}
    </CmPanel>
  )
}
